import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import Button from '../components/Button';
import LoadingSpinner from '../components/LoadingSpinner';

import LogoImage from '../assets/1.png';

export default function VerifyEmail() {
  const { t } = useI18n();
  const { verifyEmail, resendVerification } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const email = location.state?.email || '';
  const fromRegistration = location.state?.fromRegistration || false;

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Redirect if no email provided
  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  // Countdown timer for resend
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    
    if (code.length !== 6) {
      setError(t.auth.verification?.invalidCode || 'Please enter a valid 6-digit code');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await verifyEmail({ email, code });
      
      // New registrations continue directly into the short pricing setup.
      // The onboarding page still offers "Finish later" at every safe point.
      navigate(fromRegistration ? '/app/onboarding' : '/app', { replace: true });
    } catch (error: unknown) {
      console.error('Verification error:', error);
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;

      if (status === 400) {
        setError(t.auth.verification?.invalidCode || 'Invalid or expired verification code');
      } else if (status === 403) {
        // Already verified
        navigate('/login');
      } else {
        setError(t.auth.errors.networkError);
      }
      
      // Clear code on error
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (countdown > 0) return;

    setResending(true);
    setError('');
    setSuccess('');

    try {
      await resendVerification(email);
      setSuccess(t.auth.verification?.codeSent || 'Verification code sent to your email');
      setCountdown(60);
    } catch (error: unknown) {
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;
      if (status === 429) {
        setError(t.auth.verification?.tooManyRequests || 'Please wait before requesting a new code');
      } else {
        setError(t.auth.errors.networkError);
      }
    } finally {
      setResending(false);
    }
  };

  if (!email) return null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#F4EEFA] via-white to-[#E8F8F5] relative overflow-hidden py-12 px-4 sm:px-6 lg:px-8">
      {/* Background Shapes */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundSize: '40px 40px',
            backgroundImage: 'linear-gradient(to right, #e4e4e7 1px, transparent 1px), linear-gradient(to bottom, #e4e4e7 1px, transparent 1px)',
          }}
        />
        <div className="absolute top-20 left-10 w-20 h-20 bg-gradient-to-br from-[#B18AE0] to-[#5B3FD0] rounded-full opacity-20"></div>
        <div className="absolute top-40 right-20 w-16 h-16 bg-[#9FFFCB] opacity-20 rounded-xl rotate-45"></div>
        <div className="absolute bottom-32 left-20 w-14 h-14 bg-[#5B3FD0] rounded-full opacity-15"></div>
      </div>

      <div className="max-w-md w-full space-y-8 relative z-10">
        {/* Logo */}
        <div className="text-center">
          <Link to="/" className="inline-flex items-center justify-center gap-3 group mb-8">
            <img src={LogoImage} alt="EduNasser Logo" className="w-32 h-32 object-contain" />
          </Link>

          {/* Email Icon */}
          <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#5B3FD0] to-[#B18AE0] rounded-full flex items-center justify-center mb-6">
            <svg className="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>

          <h2 className="text-3xl font-bold text-[#2C2C2C]">
            {t.auth.verification?.title || 'Verify Your Email'}
          </h2>
          <p className="mt-2 text-gray-600">
            {t.auth.verification?.subtitle || 'We sent a 6-digit code to'}
          </p>
          <p className="font-semibold text-[#5B3FD0]">{email}</p>
        </div>

        {/* Verification Form */}
        <div className="bg-white/80 backdrop-blur-xl p-8 rounded-3xl shadow-2xl border border-white/50 space-y-6">
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#B18AE0] via-[#5B3FD0] to-[#9FFFCB] rounded-t-3xl"></div>

          {fromRegistration && (
            <div className="bg-green-50/80 border border-green-400 text-green-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {t.auth.verification?.registrationSuccess || 'Account created! Please verify your email.'}
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50/80 border border-red-400 text-red-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {error}
              </div>
            </div>
          )}

          {success && (
            <div className="bg-green-50/80 border border-green-400 text-green-700 px-4 py-3 rounded-xl">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                {success}
              </div>
            </div>
          )}

          {/* Code Input */}
          <div className="space-y-4">
            <div className="relative">
              <input
                type="text"
                name="code"
                value={code}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setCode(val);
                }}
                placeholder="123456"
                required
                disabled={loading}
                className="w-full text-center text-3xl tracking-[0.5em] font-bold border-2 border-gray-300 rounded-xl focus:border-[#5B3FD0] focus:ring-2 focus:ring-[#5B3FD0]/20 outline-none transition-all disabled:bg-gray-100 py-4"
              />
            </div>
          </div>

          <p className="text-center text-sm text-gray-500">
            {t.auth.verification?.codeExpiry || 'Code expires in 15 minutes'}
          </p>

          <Button
            onClick={() => handleSubmit()}
            disabled={loading || code.length !== 6}
            className="w-full shadow-lg hover:shadow-xl"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <LoadingSpinner size="sm" />
                <span>{t.common.loading}</span>
              </div>
            ) : (
              <>
                {t.auth.verification?.verifyButton || 'Verify Email'}
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </>
            )}
          </Button>

          {/* Resend */}
          <div className="text-center">
            <p className="text-sm text-gray-600">
              {t.auth.verification?.noCode || "Didn't receive the code?"}{' '}
              {countdown > 0 ? (
                <span className="text-gray-400">
                  {t.auth.verification?.resendIn || 'Resend in'} {countdown}s
                </span>
              ) : (
                <button
                  onClick={handleResend}
                  disabled={resending}
                  className="text-[#5B3FD0] hover:text-[#B18AE0] font-bold disabled:opacity-50"
                >
                  {resending ? (
                    <span className="flex items-center gap-1">
                      <LoadingSpinner size="sm" />
                      {t.common.loading}
                    </span>
                  ) : (
                    t.auth.verification?.resendButton || 'Resend Code'
                  )}
                </button>
              )}
            </p>
          </div>
        </div>

        {/* Back to Login */}
        <div className="text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-[#707070] hover:text-[#5B3FD0] font-medium"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            {t.auth.verification?.backToLogin || 'Back to Login'}
          </Link>
        </div>
      </div>
    </div>
  );
}
